/**
 * ================================================================================================
 *  WHAT BECAME OF A CHOICE MADE BEFORE SOMEBODY ARRIVED
 * ================================================================================================
 *
 * The redemption decides each recorded choice and writes the decision into
 * `core.tenant_invitation_intent.outcome`. That column is a CLOSED VOCABULARY declared in exactly
 * one place — `INTENT_OUTCOME` in `packages/db/src/customer-admin-store.ts`, which is the only code
 * that writes it — and this module is the only place those codes become words.
 *
 * ── ONE SHORT LABEL EACH, AND NOTHING ELSE. **Corrected 2026-10-28.** ──────────────────────────
 *
 * Each outcome carried a second string saying what to do about it. The Owner, looking at the
 * rendered card: *"ושוב יש המון מלל שלא רלוונטי — 'מי שמחזיק בהרשאה...' מה צריך את זה? ביקשתי ממך
 * לנקות את כל המלל המיותר."*
 *
 * He is right, and it was mine: I added those sentences in the same round in which he asked for the
 * screens to be cleaned. The label already says WHY — *the person who chose no longer has the
 * permission* — and the controls that grant it are on the same tab, three lines below. A sentence
 * telling an administrator to use the control in front of him is the product explaining itself.
 *
 * ── AND AN UNKNOWN CODE IS NAMED AS UNKNOWN ─────────────────────────────────────────────────────
 *
 * Not blank, not the raw code, and above all NOT the success wording. A cell that degrades to "we
 * do not recognise this" is honest; one that degrades to "applied" is a lie the product tells about
 * somebody's permissions.
 */

/** Whether the choice was carried out. The screen's tone and its grouping both follow from this. */
export type GrantOutcomeKind = 'applied' | 'refused' | 'unknown';

export interface GrantOutcomeText {
  readonly kind: GrantOutcomeKind;
  readonly labelHe: string;
}

/**
 * THE SEVEN CODES `INTENT_OUTCOME` DECLARES, and nothing else.
 *
 * Kept in this order deliberately: the success, then the two AUTHORITY refusals, then the three
 * structural ones, then the shape refusal. The two authority refusals are different facts leading
 * to different actions, which is why they are two codes and two labels rather than one.
 */
const OUTCOMES: Readonly<Record<string, GrantOutcomeText>> = {
  applied: { kind: 'applied', labelHe: 'הופעל' },

  refused_recorder_unauthorised: { kind: 'refused', labelHe: 'נדחה — למי שבחר אין הרשאה' },
  refused_exceeds_recorder_authority: { kind: 'refused', labelHe: 'נדחה — רחב מהרשאות מי שבחר' },
  refused_recorder_inactive: { kind: 'refused', labelHe: 'נדחה — מי שבחר אינו בארגון' },
  refused_role_missing: { kind: 'refused', labelHe: 'נדחה — התפקיד נמחק' },
  refused_company_missing: { kind: 'refused', labelHe: 'נדחה — החברה נמחקה' },
  refused_malformed: { kind: 'refused', labelHe: 'נדחה — רשומה פגומה' },
};

/** An outcome the product does not recognise. Named as such, never as a success. */
const UNKNOWN: GrantOutcomeText = { kind: 'unknown', labelHe: 'תוצאה שאינה מזוהה' };

export function grantOutcomeText(outcome: string): GrantOutcomeText {
  return OUTCOMES[outcome] ?? UNKNOWN;
}

/** Every code this module names. `grantOutcomeText.test.ts` binds it to the store's own list. */
export const NAMED_GRANT_OUTCOMES: readonly string[] = Object.keys(OUTCOMES).sort();
